import { getStorageService, initializeStorageService } from '../services/storage/storageServiceInitializer';
import { getStorage, ref, deleteObject, getMetadata } from "firebase/storage";

const validateBlob = (blob) => {
  if (!blob) {
    throw new Error('Image blob is required');
  }

  if (!(blob instanceof Blob)) {
    throw new Error('Image must be a valid blob');
  }

  if (!blob.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  const maxSize = 10 * 1024 * 1024;
  if (blob.size > maxSize) {
    throw new Error('Image size must be less than 10MB');
  }

  return blob;
};

const validateEntityType = (entityType) => {
  if (!entityType || typeof entityType !== 'string' || entityType.trim() === '') {
    throw new Error('Entity type is required and must be a valid string');
  }

  const validTypes = ['card', 'lead', 'contact-link', 'user', 'company', 'product', 'subteam', 'template', 'team'];
  const validType = entityType.trim().toLowerCase();

  if (!validTypes.includes(validType)) {
    throw new Error(`Entity type must be one of: ${validTypes.join(', ')}`);
  }

  return validType;
};

const validateSubType = (subType) => {
  if (!subType || typeof subType !== 'string' || subType.trim() === '') {
    throw new Error('Sub type is required and must be a valid string');
  }

  const validSubTypes = ['profile', 'cover', 'logo', 'banner', 'icon', 'gallery', 'background'];
  const validSubType = subType.trim().toLowerCase();

  if (!validSubTypes.includes(validSubType)) {
    throw new Error(`Sub type must be one of: ${validSubTypes.join(', ')}`);
  }

  return validSubType;
};

const validateEntityId = (entityId) => {
  if (entityId !== null && entityId !== undefined) {
    if (typeof entityId !== 'string' || entityId.trim() === '') {
      throw new Error('Entity ID must be a valid string');
    }
    return entityId.trim();
  }
  return null;
};

const validateFileName = (fileName) => {
  if (!fileName || typeof fileName !== 'string' || fileName.trim() === '') {
    throw new Error('File name is required and must be a valid string');
  }

  const fileNameRegex = /^[a-zA-Z0-9._-]+$/;
  if (!fileNameRegex.test(fileName.trim())) {
    throw new Error('File name contains invalid characters');
  }

  return fileName.trim();
};

const validateSetUrlFunction = (setUrl) => {
  if (setUrl && typeof setUrl !== 'function') {
    throw new Error('setUrl must be a function');
  }
  return setUrl;
};

const handleImageStorageError = (error, operation = 'API operation') => {
  if (error.code === 'storage/unauthorized') {
    throw new Error('You do not have permission to access this storage location');
  }

  if (error.code === 'storage/canceled') {
    throw new Error('Upload was canceled');
  }

  if (error.code === 'storage/unknown') {
    throw new Error('Unknown storage error occurred');
  }

  if (error.code === 'storage/invalid-argument') {
    throw new Error('Invalid argument provided to storage operation');
  }

  if (error.code === 'storage/not-found') {
    throw new Error('File not found in storage');
  }

  if (error.code === 'storage/already-exists') {
    throw new Error('File already exists in storage');
  }

  if (error.code === 'storage/permission-denied') {
    throw new Error('Permission denied for storage operation');
  }

  if (error.code === 'storage/resource-exhausted') {
    throw new Error('Storage quota exceeded');
  }

  if (error.code === 'storage/failed-precondition') {
    throw new Error('Storage operation failed due to precondition not met');
  }

  if (error.code === 'storage/aborted') {
    throw new Error('Storage operation was aborted');
  }

  if (error.code === 'storage/out-of-range') {
    throw new Error('Storage operation out of range');
  }

  if (error.code === 'storage/unimplemented') {
    throw new Error('Storage operation not implemented');
  }

  if (error.code === 'storage/internal') {
    throw new Error('Internal storage error occurred');
  }

  if (error.code === 'storage/unavailable') {
    throw new Error('Storage service is currently unavailable');
  }

  if (error.code === 'storage/data-loss') {
    throw new Error('Data loss occurred during storage operation');
  }

  if (error.code === 'ERR_NETWORK') {
    throw new Error('Network error. Please check your connection and try again.');
  }

  if (error.code === 'ECONNABORTED') {
    throw new Error('Request timeout. Please try again.');
  }

  const errorMessage = error.message || 'An unexpected error occurred during storage operation';
  throw new Error(errorMessage);
};

const constructStoragePath = (entityType, subType, entityId) => {
  const validEntityType = validateEntityType(entityType);
  const validSubType = validateSubType(subType);
  const validEntityId = validateEntityId(entityId);

  let path;
  if (validEntityType === 'card') {
    path = `cards/${validSubType}/${validEntityId || Date.now()}.png`;
  } else if (validEntityType === 'lead') {
    path = `leads/profile/${validEntityId || Date.now()}.png`;
  } else if (validEntityType === 'contact-link') {
    path = `contact-links/${validEntityId || Date.now()}.png`;
  } else if (validEntityType === 'subteam') {
    path = `subteams/${validSubType}/${validEntityId || Date.now()}.png`;
  } else if (validEntityType === 'template') {
    path = `templates/${validSubType}/${validEntityId || Date.now()}.png`;
  } else if (validEntityType === 'team') {
    path = `team-assets/${validSubType}/${validEntityId || Date.now()}.png`;
  } else {
    path = `${validEntityType}/${validSubType}/${validEntityId || Date.now()}.png`;
  }

  return path;
};

export const uploadImageBlob = async (blob, setUrl, entityType = 'card', subType = 'profile', entityId = null) => {
  try {
    const validBlob = validateBlob(blob);
    const validSetUrl = validateSetUrlFunction(setUrl);
    const path = constructStoragePath(entityType, subType, entityId);

    await initializeStorageService();
    const storageService = getStorageService();

    const file = new File([validBlob], path, { type: validBlob.type || 'image/png' });

    const fileUrl = await storageService.uploadFile(file);

    if (validSetUrl) {
      validSetUrl(fileUrl);
    }

    return fileUrl;
  } catch (error) {
    handleImageStorageError(error, 'Uploading image blob');
  }
};

export const deleteImage = async (entityType, subType, entityId) => {
  try {
    const path = constructStoragePath(entityType, subType, entityId);

    const storage = getStorage();
    const fileRef = ref(storage, path);

    try {
      await getMetadata(fileRef);
      await deleteObject(fileRef);
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        return true;
      }
      throw error;
    }

    return true;
  } catch (error) {
    handleImageStorageError(error, 'Deleting image');
  }
};

export const deleteOldImage = async (fileName) => {
  try {
    const validFileName = validateFileName(fileName);

    const storage = getStorage();
    const fileRef = ref(storage, validFileName);

    try {
      await getMetadata(fileRef);
      await deleteObject(fileRef);
    } catch (error) {
      if (error.code === 'storage/object-not-found') {
        return true;
      }
      throw error;
    }

    return true;
  } catch (error) {
    handleImageStorageError(error, 'Deleting old image');
  }
};